import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { Message, MessageDraft } from '../../models';
import { ChatStrings } from '../../i18n';
import { resolveRetryTarget, selectVisibleMessages, turnKeyOf } from '../../services/reconcile';
import MessageItem from '../MessageItem';
import TypedDraft from '../TypedDraft';
import WaitLine from '../WaitLine';

interface MessageListProps {
   messages: Message[];
   isBotTyping: boolean;
   /** A turn waiting for its answer has a draft: the wait line says it is still being written. */
   isBotWriting?: boolean;
   strings: ChatStrings;
   /** clientTurnIds currently waiting for an answer: no Retry is offered for those. */
   awaitingTurns?: string[];
   /** The answers still being written, in the order their turns were sent; each shows where its answer will land. */
   drafts?: MessageDraft[];
   onRetry?: (messageId: string) => void;
   /** The guest is signed in for this hotel: the cover opens the column. */
   canTypeMessages: boolean;
   hotelName?: string;
   /** Example questions from the hotel's own content; shown only before the first message. */
   suggestedQuestions?: string[];
   onSuggestion?: (question: string) => void;
   /** Whether the cover's name is still in view (the header capsule takes the name when it is not). */
   onCoverVisible?: (visible: boolean) => void;
}

const NO_DRAFTS: MessageDraft[] = [];

/** How close to the end of the column (px) still counts as being at the end. */
const NEAR_END_PX = 80;

/** Following a draft that grows every frame: no smooth scroll trailing behind it (TypeScript 4.9's DOM types lack 'instant'). */
const INSTANT = 'instant' as ScrollBehavior;

/**
 * The column of entries under the floating capsules. The cover (the hotel's name set large, the greeting) opens
 * it and scrolls under the header; each guest message introduces a page; the newest entry sits above the composer.
 */
const MessageList: React.FC<MessageListProps> = ({
   messages,
   isBotTyping,
   isBotWriting = false,
   strings,
   awaitingTurns = [],
   drafts = NO_DRAFTS,
   onRetry,
   canTypeMessages,
   hotelName,
   suggestedQuestions = [],
   onSuggestion,
   onCoverVisible
}) => {
   const columnRef = useRef<HTMLDivElement | null>(null);
   const coverNameRef = useRef<HTMLHeadingElement | null>(null);
   const endRef = useRef<HTMLDivElement | null>(null);

   // Superseded ProviderError entries are hidden here, not removed from state.
   const visibleMessages = useMemo(() => selectVisibleMessages(messages), [messages]);

   // Turns whose answer was drafted on screen: that answer takes its draft's place.
   const draftedTurnsRef = useRef(new Set<string>());
   useEffect(() => {
      drafts.forEach((draft) => draftedTurnsRef.current.add(draft.turnKey));
   }, [drafts]);
   const replacesDraft = (msg: Message): boolean => msg.sender === 'bot' && draftedTurnsRef.current.has(turnKeyOf(msg) ?? '');

   // Whether the guest is following the end of the column: until they scroll up (even a little, since a typing draft is
   // followed every frame), and again once they scroll back down near it. Scrolling down (ours too) never stops it, nor
   // does the column growing under them, or shrinking (the browser then moves them up, but to the very end).
   const followRef = useRef(true);
   useEffect(() => {
      const column = columnRef.current;
      if (!column) return;
      let lastTop = column.scrollTop;
      const onScroll = () => {
         const top = column.scrollTop;
         const toEnd = column.scrollHeight - top - column.clientHeight;
         if (top < lastTop && toEnd > 1) followRef.current = false;
         else if (top > lastTop && toEnd <= NEAR_END_PX) followRef.current = true;
         lastTop = top;
      };
      column.addEventListener('scroll', onScroll, { passive: true });
      return () => column.removeEventListener('scroll', onScroll);
   }, []);

   // Follow the newest entry. An answer that took its draft's place is followed only like its draft: from the end.
   const newest = visibleMessages[visibleMessages.length - 1];
   const newestReplacedDraft = !!newest && replacesDraft(newest);
   useEffect(() => {
      if (newestReplacedDraft && !followRef.current) return;
      endRef.current?.scrollIntoView({ block: 'end' });
   }, [visibleMessages.length, isBotTyping, newestReplacedDraft]);

   // Follow a growing draft only while the guest is at the end: never pull them back from what they scrolled up to read.
   // Called by the draft before paint as it types, so the end stays in view frame by frame.
   const followDraft = useCallback(() => {
      if (followRef.current) endRef.current?.scrollIntoView({ block: 'end', behavior: INSTANT });
   }, []);

   const showCover = canTypeMessages;

   // The header capsule takes the name once the cover's name has scrolled under it: measured on every scroll and
   // resize, so the state is always what is on screen (an observer's first report was not).
   useEffect(() => {
      const column = columnRef.current;
      const name = coverNameRef.current;
      if (!showCover || !column || !name) {
         onCoverVisible?.(false);
         return;
      }
      let frame = 0;
      const measure = () => {
         frame = 0;
         const headerHeight = parseFloat(getComputedStyle(column).getPropertyValue('padding-top')) || 0;
         const nameBottom = name.getBoundingClientRect().bottom - column.getBoundingClientRect().top;
         onCoverVisible?.(nameBottom > headerHeight);
      };
      const schedule = () => {
         if (!frame) frame = requestAnimationFrame(measure);
      };
      measure();
      column.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      return () => {
         if (frame) cancelAnimationFrame(frame);
         column.removeEventListener('scroll', schedule);
         window.removeEventListener('resize', schedule);
      };
   }, [showCover, onCoverVisible, visibleMessages.length]);

   // Retry is offered on a visible ProviderError entry or a failed send, only when the turn's original
   // clientTurnId is known and the turn is not already waiting for an answer.
   const canRetry = (msg: Message): boolean => {
      const isError = msg.sender === 'bot' && msg.outcome === 'ProviderError';
      const isFailedSend = msg.sender === 'user' && msg.deliveryStatus === 'failed' && msg.failureKind !== 'conflict';
      if ((!isError && !isFailedSend) || !msg.id) return false;
      const target = resolveRetryTarget(messages, msg.id);
      return !!target && !awaitingTurns.includes(target.clientTurnId);
   };

   const hasConversation = visibleMessages.some((m) => m.sender === 'user' || (m.sender === 'bot' && !m.notice));
   const showSuggestions = canTypeMessages && !hasConversation && suggestedQuestions.length > 0;

   return (
      <div className="cx-column" ref={columnRef}>
         <div className="cx-measure cx-column__inner">
            {showCover && (
               <section className="cx-cover" aria-label={strings.greeting(hotelName)}>
                  <h1 className="cx-cover__name" dir="auto" ref={coverNameRef}>
                     {hotelName ?? strings.conciergeTitle}
                  </h1>
                  <p className="cx-cover__kind">{hotelName ? strings.headerSub : strings.byXenioAI}</p>
                  <h2 className="cx-greeting__title" dir="auto">
                     {strings.greeting(hotelName)}
                  </h2>
                  <p className="cx-greeting__hint">{strings.greetingHint}</p>
                  {showSuggestions && (
                     <ul className="cx-suggestions" aria-label={strings.suggestionsLabel}>
                        {suggestedQuestions.slice(0, 3).map((question) => (
                           <li key={question}>
                              <button type="button" className="cx-chip cx-frosted" dir="auto" onClick={() => onSuggestion?.(question)}>
                                 {question}
                              </button>
                           </li>
                        ))}
                     </ul>
                  )}
               </section>
            )}

            {visibleMessages.map((msg, index) => (
               <MessageItem
                  key={msg.renderKey || msg.id || `${msg.timestamp}-${index}`}
                  message={msg}
                  strings={strings}
                  canRetry={canRetry(msg)}
                  onRetry={onRetry}
                  inPlace={replacesDraft(msg)}
               />
            ))}

            {/* Each draft keeps one key per turn, so it updates in place (and types on) as the answer grows */}
            {drafts.map((draft) => (
               <TypedDraft key={`draft-${draft.turnKey}`} draft={draft} strings={strings} onGrow={followDraft} />
            ))}

            <WaitLine active={isBotTyping} label={isBotWriting ? strings.writing : strings.waiting} />

            <div ref={endRef} aria-hidden="true" />
         </div>
      </div>
   );
};

export default MessageList;
